/**
 * Chapters belong to the broadcast, not the clip, so a version has to snapshot them separately
 * from `asset_metadata` and put them back through a different endpoint.
 */

import { assetsChaptersBulkAddUpdateCreate, type ChapterResponse } from '@goldcast/api/content';
import appLogger from '@/services/logger';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { showErrorToast } from '@/libs/toast/toast';
import { currentClip } from '@/stores/clip';
import { sharedAPI } from '@/stores/sharedAPI/sharedAPI';
import { addPendingUpdate, PendingEditType } from '@/stores/pendingEditsStore';

/** The broadcast chapters the editor currently has loaded for a clip. */
export function getCurrentChapters(clipId: string): ChapterResponse[] | undefined {
  return sharedAPI.getSnapshot()[clipId]?.transcriptStore?.chapterData;
}

/**
 * Swaps the chapters the editor is showing. Local only — nothing is written, so this is safe
 * for previewing a version and is reversed by applying the previous set back.
 */
export function applyChaptersLocally(chapters: ChapterResponse[] | undefined): void {
  EventBus.dispatch(CustomEvents.UpdateChapters, {
    chapterData: chapters,
    chaptersLastUpdatedAt: new Date().toISOString()
  });
}

/**
 * Writes chapters back to the broadcast, then reflects them locally.
 *
 * This replaces the chapter list for **every clip cut from the same recording** — call it only
 * for an explicit restore, never for a preview. Mirrors the undo/redo replay path in
 * clipHistory.
 */
export function persistChapters(clipId: string, chapters: ChapterResponse[]): void {
  applyChaptersLocally(chapters);

  const broadcastId = currentClip.getSnapshot()[clipId]?.broadcast;
  if (!broadcastId) {
    return;
  }

  const updatePromise = assetsChaptersBulkAddUpdateCreate({
    body: chapters,
    queryParams: { content_id: broadcastId }
  });

  addPendingUpdate({ clipId, updatePromise, version: Date.now(), editType: PendingEditType.chapter });

  updatePromise.catch(err => {
    appLogger.error('[versioning] failed to restore chapters', err);
    showErrorToast('Failed to restore chapters. Please try again.');
  });
}
